import classNames from 'classnames';
import { Formik } from 'formik';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import SwiperCore from 'swiper';
import { SwiperSlide } from 'swiper/react';
import * as Yup from 'yup';

import { Back } from '@/components/Back';
import { Button, ButtonTypes } from '@/components/Button';
import { Stepper } from '@/components/Stepper';
import { SwiperBlock } from '@/components/carouselsBlocks/SwiperBlock';
import { InputSelect } from '@/components/form/InputSelect';
import { ArrowIcon } from '@/components/icons/ArrowIcon';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useTrackPersonalization } from '@/modules/analytic/hooks/useTrackPersonalization';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { usePersonalization } from '@/modules/onboarding/hooks/usePersonalization';
import { OnboardingCategorySection } from '@/modules/onboarding/presentation/OnboardingCategorySection';

interface OnboardingModalProps {
  close: () => void;
}
export const OnboardingModal = ({ close }: OnboardingModalProps) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [sliderEnded, setSliderEnd] = useState(false);
  const { user } = useAuth();
  const { formatMessage } = useIntl();
  const scrollDiv = useRef(null);
  const { scrollPosition, scrollTo } = useScrollPosition(scrollDiv);
  const { trackPersonalization } = useTrackPersonalization();
  const { showNotificationError, showNotificationInfo } = useNotifications();
  const {
    categories,
    jobCategories,
    isLoadingCategories,
    isLoadingJobCategories,
    updateUserAsync,
  } = usePersonalization();

  useEffect(() => {
    document.body.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const validationSchema = Yup.object().shape({
    corpoJobCategory: user.isCorporate ? Yup.string().required() : Yup.string(),
    categories: Yup.array().required().min(2),
  });

  const moveJobCategoryInCategories = ({
    corpoJobCategory,
    categories,
  }: {
    corpoJobCategory: string;
    categories: string[];
  }) => {
    return [...categories.filter((category) => !category.includes('jobcat/')), corpoJobCategory];
  };

  const initialValues = useMemo(
    () => ({
      categories: user?.categories || [],
      corpoJobCategory: user?.corpoJobCategory || '',
    }),
    [user]
  );

  const jobCategoriesOptions = useMemo(
    () => [
      ...(jobCategories?.[0]?.items || []).map(({ title, user_tag }) => ({
        text: title,
        value: user_tag,
      })),
    ],
    [jobCategories]
  );

  const onSubmit = async ({
    corpoJobCategory,
    categories,
  }: {
    corpoJobCategory: string;
    categories: string[];
  }) => {
    if (isLoadingCategories || isLoadingJobCategories) return;

    try {
      await updateUserAsync({
        userId: user.UUID,
        data: {
          categories: moveJobCategoryInCategories({ categories, corpoJobCategory }).filter(
            (cat) => !!cat
          ),
        },
      });

      trackPersonalization({
        corpoJobCategory,
        categories,
        hasReset: false,
      });

      showNotificationInfo('onboarding.categories.success.notification');
      close();
    } catch (error) {
      showNotificationError('onboarding.categories.error.notification');
    }
  };

  const handleSlideNext = useCallback(() => {
    swiper?.slideNext();
  }, [swiper]);

  const handleSlidePrev = useCallback(() => {
    swiper?.slidePrev();
  }, [swiper]);

  const isLoading = isLoadingCategories || isLoadingJobCategories;

  return createPortal(
    <div className="modal">
      <div className="modal-bg" />
      <div
        className={classNames('modal-container relative', {
          scrollable: sliderEnded,
        })}
        aria-modal="true"
      >
        {swiper?.slides?.length > 1 && (
          <Stepper step={swiper?.activeIndex + 1} steps={swiper?.slides?.length} />
        )}

        {isLoading ? null : (
          <>
            <div className="modal-header mx-auto mt-2 flex justify-center items-center">
              {swiper?.slides?.length > 1 && sliderEnded && (
                <Back onClick={handleSlidePrev} className="top-5 absolute top-1 left-10 z-10" />
              )}
              <Image
                src="/logo-majelan.png"
                alt="Majelan logo"
                className="h-10"
                width={126}
                height={32}
              />
            </div>
            <div className="modal-content mx-auto mt-5">
              <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={onSubmit}
                validationSchema={validationSchema}
                validateOnMount
                validateOnChange
                initialStatus="form"
              >
                {({ isValid, values, handleSubmit, isSubmitting }) => {
                  return (
                    <form className="relative w-full">
                      <SwiperBlock
                        onInit={(swiperCore) => {
                          setSliderEnd(user.isCorporate ? false : swiperCore.isEnd);
                          setSwiper(swiperCore);
                        }}
                        onSlideChange={(swiper) => {
                          setSliderEnd(swiper.isEnd);
                          scrollTo(0);
                        }}
                        slidesPerView={1}
                        spaceBetween={0}
                        centeredSlides={true}
                        navigation={false}
                        loop={false}
                        pagination={false}
                        autoplay={false}
                        allowTouchMove={false}
                        freeMode
                        autoHeight
                      >
                        {user.isCorporate && (
                          <SwiperSlide className="overflow-y-hidden pt-20">
                            <div className="max-w-lg mx-auto">
                              <div className="max-w-sm mx-auto text-center mb-8">
                                <h1 className="title-h2 mt-0 mb-2">
                                  {formatMessage({ id: 'onboarding.header.title' })}
                                </h1>
                                <p className="text-brand-neutral-300 text-sm">
                                  {formatMessage({ id: 'onboarding.header.description' })}
                                </p>
                              </div>
                              <h3 className="title-h4 mb-1">
                                {formatMessage({
                                  id: 'onboarding.jobCategories.header.title',
                                })}
                              </h3>
                              <p className="text-brand-neutral-300 text-sm">
                                {formatMessage({
                                  id: 'onboarding.jobCategories.header.description',
                                })}
                              </p>

                              <div className="w-full my-6 2xl:text-base text-left">
                                <InputSelect
                                  name="corpoJobCategory"
                                  options={jobCategoriesOptions}
                                  value={values.corpoJobCategory}
                                  placeholder={formatMessage({
                                    id: 'onboarding.jobCategories.placeholder',
                                  })}
                                />
                              </div>
                            </div>
                          </SwiperSlide>
                        )}
                        <SwiperSlide className="relative flex flex-col h-[calc(100vh-200px)] max-h-[600px]">
                          <div ref={scrollDiv} className="overflow-y-auto py-20">
                            <div className="max-w-lg mx-auto text-center mb-5">
                              {!user.isCorporate ? (
                                <>
                                  <div className="max-w-sm mx-auto text-center mb-8">
                                    <h1 className="title-h2 mt-0 mb-2">
                                      {formatMessage({ id: 'onboarding.header.title' })}
                                    </h1>
                                    <p className="text-brand-neutral-300 text-sm">
                                      {formatMessage({ id: 'onboarding.header.description' })}
                                    </p>
                                  </div>
                                  <div className="text-start">
                                    <h2 className="font-bold">
                                      {formatMessage({
                                        id: 'onboarding.categories.header.title',
                                      })}
                                    </h2>
                                    <p className="text-brand-neutral-300 text-sm">
                                      {formatMessage({
                                        id: 'onboarding.categories.header.description',
                                      })}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <h3 className="title-h2 mt-0 mb-2">
                                    {formatMessage({
                                      id: 'onboarding.categories.header.title',
                                    })}
                                  </h3>
                                  <p className="text-brand-neutral-300 text-sm">
                                    {formatMessage({
                                      id: 'onboarding.categories.header.description',
                                    })}
                                  </p>
                                </>
                              )}
                            </div>
                            <div className="max-w-lg mx-auto">
                              {categories.map((category, index) => (
                                <OnboardingCategorySection
                                  fieldName="categories"
                                  category={category}
                                  key={`categorie-onboarding-${index}`}
                                  value={values.categories}
                                  multiple
                                />
                              ))}
                            </div>
                          </div>
                          <div className="w-full absolute bottom-0 flex justify-center h-20 scrollable-bottom-dark-shadow">
                            <ArrowIcon
                              className={classNames('transition-all duration-300 ease-out mt-5', {
                                'opacity-0': scrollPosition > 10,
                                hidden: scrollPosition > 50,
                              })}
                            />
                          </div>
                        </SwiperSlide>
                      </SwiperBlock>
                      <div className="flex justify-center">
                        <div className="w-full max-w-md mb-10">
                          {!sliderEnded ? (
                            <Button
                              style="primary"
                              type={ButtonTypes.Button}
                              fullWidth
                              onClick={handleSlideNext}
                              disabled={
                                isSubmitting || (user.isCorporate && !values.corpoJobCategory)
                              }
                              className="!mb-0"
                            >
                              {formatMessage({
                                id: 'global.continue',
                              })}
                            </Button>
                          ) : (
                            <Button
                              style="primary"
                              type={ButtonTypes.Button}
                              fullWidth
                              onClick={handleSubmit}
                              disabled={isSubmitting || !isValid}
                              className="!mb-0"
                            >
                              {formatMessage({
                                id: 'global.end',
                              })}
                            </Button>
                          )}
                        </div>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          </>
        )}
      </div>
    </div>,
    document.getElementById('genericPortal')
  );
};
